// TASK-582: off-dashboard canvas surface. The generative-UI canvas (`<Canvas />`) only
// mounts on the dashboard (DashboardPage), so a widget summoned via open_view from a
// client page landed in canvasStore with nowhere to render. This slide-over gives those
// summons a home on every OTHER route, reusing the same store-backed <Canvas /> — no new
// render logic. On the dashboard it renders nothing (the centre-column Canvas owns it).
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, LayoutGrid } from 'lucide-react';
import { Canvas } from './Canvas';
import { useCanvasStore, CANVAS_DRAWER_GEOMETRY } from '../../stores/canvasStore';

export function CanvasDrawer() {
  const location = useLocation();
  const specs = useCanvasStore((s) => s.specs);
  const grid = specs.filter((t) => !t.rail); // rail tiles dock in the dashboard DO column
  const [dismissed, setDismissed] = useState(false);

  const onDashboard = location.pathname.startsWith('/dashboard');

  // Auto-open when a new summon appends a tile (so it never lands silently in the store).
  // TASK-768: dock-side creation results no longer arrive here at all — the dock
  // intercepts `ArtifactCard` widgets and renders them inline, the way the rail
  // has since TASK-742. What reaches this store is a genuine `open_view` summon
  // with no inline home, which is what auto-open was built for.
  const prevCount = useRef(grid.length);
  useEffect(() => {
    if (grid.length > prevCount.current) setDismissed(false);
    prevCount.current = grid.length;
  }, [grid.length]);

  // TASK-768: publish the expanded state so the dock's TranscriptSheet can step
  // aside instead of being clipped mid-sentence under this slide-over.
  const expanded = !onDashboard && grid.length > 0 && !dismissed;
  const setDrawerOpen = useCanvasStore((s) => s.setDrawerOpen);
  useEffect(() => {
    setDrawerOpen(expanded);
  }, [expanded, setDrawerOpen]);
  // TASK-790: unmount-only, in its own effect. The reset used to be the cleanup
  // of the effect above, which is keyed on `expanded` — so it ran on every
  // toggle, writing false then true. Harmless (React batches both into one
  // commit, and the only consumer reads through a hook selector), but it held
  // by batching rather than by saying what it means.
  useEffect(() => () => setDrawerOpen(false), [setDrawerOpen]);

  if (onDashboard || grid.length === 0) return null;

  // Dismissed but tiles still present: a small pill to bring the canvas back.
  if (dismissed) {
    return (
      <button
        type="button"
        onClick={() => setDismissed(false)}
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm shadow-lg transition-colors hover:bg-muted"
      >
        <LayoutGrid className="h-4 w-4" />
        Canvas ({grid.length})
      </button>
    );
  }

  return (
    // TASK-790: the width comes from the constant the TranscriptSheet's
    // step-aside offset is defined beside, so the two cannot drift apart.
    <div
      className={`fixed inset-y-0 right-0 z-40 flex w-full ${CANVAS_DRAWER_GEOMETRY.width} flex-col border-l border-border bg-background shadow-2xl`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-medium">
          <LayoutGrid className="h-4 w-4" />
          Canvas
        </span>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Hide canvas"
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <Canvas />
      </div>
    </div>
  );
}
